import { useState } from 'react';
import { Link } from '@tanstack/react-router';

import { ApiError } from '../api/client';
import { ErrorBanner } from '../components/ErrorBanner';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import { DoneProposals, PendingProposals, TeamCards } from '../features/agents/AgentBoardLists';
import { LUNA, ReceptionPanel } from '../features/agents/reception/ReceptionPanel';
import { AgentStage } from '../features/agents/AgentStage';
import { ProposalDrawer } from '../features/agents/ProposalDrawer';
import { pendingCount, useAgentsBoard, useReplaceProposal } from '../features/agents/useAgentsBoard';

/**
 * Dale Agents (Max and Ultra, the same team; Ultra only gets more use). The
 * four agents prepare an oferta or a message, the owner edits and approves it,
 * and nothing goes out without that approval. Their reports live in Informes.
 */
export function AgentesPage() {
  const boardQuery = useAgentsBoard();
  const replaceProposal = useReplaceProposal();
  const [showReception, setShowReception] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const board = boardQuery.data;
  const open = board?.proposals.find((proposal) => proposal.id === openId) ?? null;
  const pending = pendingCount(board);

  if (showReception) {
    return (
      <main className="agents-page">
        <PageHeader title={LUNA.name} description={LUNA.role} actions={
          <button type="button" className="btn small secondary" onClick={() => setShowReception(false)}>Volver al equipo</button>
        } />
        <ReceptionPanel />
      </main>
    );
  }

  return (
    <main className="agents-page">
      <PageHeader title="Dale Agents" description="Tu equipo prepara el trabajo. Nada sale sin tu aprobación." actions={
        <button type="button" className="btn small secondary" onClick={() => setShowReception(true)}>Luna, recepción</button>
      } />
      <p>
        <Link to="/inteligencia" className="linklike intel-agents-report-link">
          Ver el último informe de tu equipo
        </Link>
      </p>

      {boardQuery.isLoading ? <LoadingState label="Llamando a tu equipo…" /> : null}
      {boardQuery.isError ? (
        <>
          <ErrorBanner message={errorMessage(boardQuery.error)} />
          <button type="button" className="btn small secondary" disabled={boardQuery.isFetching} onClick={() => void boardQuery.refetch()}>
            Reintentar
          </button>
        </>
      ) : null}

      {board ? (
        <>
          <AgentStage board={board} onReview={setOpenId} />

          <section className="section" aria-labelledby="agents-pending">
            <h2 className="section-label" id="agents-pending">
              Para revisar{pending > 0 ? ` (${pending})` : ''}
            </h2>
            <PendingProposals board={board} onOpen={setOpenId} />
          </section>

          <section className="section" aria-labelledby="agents-team">
            <h2 className="section-label" id="agents-team">Tu equipo</h2>
            <TeamCards agents={board.agents} />
          </section>

          {board.proposals.some((proposal) => proposal.status !== 'pending') ? (
            <section className="section" aria-labelledby="agents-done">
              <h2 className="section-label" id="agents-done">Hecho</h2>
              <DoneProposals board={board} onOpen={setOpenId} />
            </section>
          ) : null}

          <ProposalDrawer
            proposal={open}
            agents={board.agents}
            onClose={() => setOpenId(null)}
            onSettled={replaceProposal}
            onReload={() => void boardQuery.refetch()}
          />
        </>
      ) : null}
    </main>
  );
}

function errorMessage(error: unknown): string {
  return error instanceof ApiError ? error.message : 'No pudimos llamar a tu equipo. Intenta de nuevo.';
}
